import React from "react";
import Button from "../../../../components/staff/Button";
import CheckBox from "../../../../components/staff/CheckBox";
import InputText from "../../../../components/staff/InputText";
import TrashIcon from "../../../../components/Atomos/Icons/EditMode/Trash";
import {
  staffFieldControlSlot,
  staffFieldRoot,
} from "../../../../components/staff/fieldStyles";
import {
  IRegistrationGuest,
  MAX_REGISTRATION_GUESTS,
} from "../../../../interfaces/models/IRegistration";
import { formatTimestampDate } from "../../../../utils/dates";
import {
  canRemoveUnsavedGuest,
  sanitizeGuestsForPersist,
} from "../../../../utils/registrationGuests";
import type { SpecialEventProgramDisplay } from "../specialEventProgramDisplay";

type SpecialEventProgramCardProps = {
  display: SpecialEventProgramDisplay;
  isSelected: boolean;
  programGuests: IRegistrationGuest[];
  persistedProgramGuests: IRegistrationGuest[];
  guests: IRegistrationGuest[];
  showGuestValidationErrors?: boolean;
  registrantFieldsDisabled: boolean;
  onToggle: (programId: string, checked: boolean) => void;
  onUpdateGuest: (
    programId: string,
    guestIndex: number,
    patch: Partial<IRegistrationGuest>,
  ) => void;
  onRemoveGuest: (programId: string, guestIndex: number) => void;
  onCopyMainGuests: (programId: string) => void;
  onAddGuest: (programId: string) => void;
};

function DetailField ({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  return (
    <div className="text-sm text-gray-font w-fit">
      <span className="font-semibold">{label}: </span>
      {value}
    </div>
  );
}

export default function SpecialEventProgramCard ({
  display,
  isSelected,
  programGuests,
  persistedProgramGuests,
  guests,
  showGuestValidationErrors = false,
  registrantFieldsDisabled,
  onToggle,
  onUpdateGuest,
  onRemoveGuest,
  onCopyMainGuests,
  onAddGuest,
}: SpecialEventProgramCardProps): React.ReactElement {
  const { programId, title, date, timeRange, feeLabel, guestFeeLabel } = display;

  return (
    <div
      className={`grid grid-cols-1 gap-6 items-start border-b border-gray-200 pb-6 last:border-b-0 last:pb-0 ${isSelected ? "lg:grid-cols-2" : ""
        }`}
    >
      <div className="flex flex-col gap-3 min-w-0">
        <CheckBox
          checked={isSelected}
          disabled={registrantFieldsDisabled}
          onCheckedChange={(checked) => onToggle(programId, checked)}
          label={title}
          labelClassName="font-medium"
        />

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <DetailField label="Date" value={date} />
          <DetailField label="Time" value={timeRange} />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <DetailField label="Fee" value={feeLabel} />
          <DetailField label="Guest Fee" value={guestFeeLabel} />
        </div>
      </div>

      {isSelected && (
        <div className="flex flex-col gap-4 min-w-0">
          <div className="flex flex-col gap-6">
            {programGuests.map((guest, guestIndex) => (
              <div
                key={guest.id}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-3 gap-4 w-full"
              >
                <div className={staffFieldRoot}>
                  <label className="font-semibold text-gray-font">
                    Modification Date
                  </label>
                  <div className={staffFieldControlSlot}>
                    <span className="px-1 text-gray-font">
                      {formatTimestampDate(
                        guest.modificationDate ?? guest.registrationDate,
                      )}
                    </span>
                  </div>
                </div>
                <InputText
                  title="Guest Name"
                  required
                  value={guest.name}
                  onChange={(value) => {
                    onUpdateGuest(programId, guestIndex, { name: value });
                  }}
                  disabled={registrantFieldsDisabled}
                  showAsLabel={registrantFieldsDisabled}
                  error={showGuestValidationErrors && !guest.name.trim()}
                  errorMessage="Guest name is required."
                />
                <div className="flex gap-4 sm:col-span-2 lg:col-span-1 xl:col-span-1">
                  <InputText
                    title="Special Requests"
                    value={guest.specialRequest ?? ""}
                    onChange={(value) => {
                      onUpdateGuest(programId, guestIndex, {
                        specialRequest: value,
                      });
                    }}
                    disabled={registrantFieldsDisabled}
                    showAsLabel={registrantFieldsDisabled}
                    className="flex-1"
                  />
                  {!registrantFieldsDisabled &&
                    canRemoveUnsavedGuest(guest.id, persistedProgramGuests) && (
                    <Button
                      onClick={() => onRemoveGuest(programId, guestIndex)}
                      color="primary"
                      variant="filled"
                      size="fixed"
                      className="mt-auto !w-auto shrink-0"
                      icon={<TrashIcon className="w-4 h-4 text-white" />}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {!registrantFieldsDisabled && (
            <div className="flex flex-wrap justify-start lg:justify-end gap-3">
              <Button
                text="Use Main Guests"
                onClick={() => onCopyMainGuests(programId)}
                color="secondary"
                variant="outlined"
                size="fixed"
                disabled={sanitizeGuestsForPersist(guests).length === 0}
              />
              <Button
                text="Add Guest"
                onClick={() => onAddGuest(programId)}
                color="secondary"
                variant="filled"
                size="fixed"
                disabled={programGuests.length >= MAX_REGISTRATION_GUESTS}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
